import { AppThunk, RootState } from ".";
import { createRequestStateAdapter } from "./util/requestStateAdapter";
import { ProjectId } from "../backend/model";
import { createSlice, Draft, PayloadAction } from "@reduxjs/toolkit";
import { API, EndpointSummary } from "../backend/api";
import {
  GlobalKDrainProjectSummary,
  DetailedKDrainProjectSummary,
} from "../backend/summary";
import { updateErrorNotification } from "./notificationSlice";

const totalDashboardId = -1;
const requestStateAdapter = createRequestStateAdapter();

type State = {
  ids: number[];
  project: { [id: number]: DetailedKDrainProjectSummary };
  total: { [year: number]: GlobalKDrainProjectSummary };
} & typeof requestStateAdapter.initialState;

const initialState: State = {
  ids: [],
  project: {},
  total: {},
  ...requestStateAdapter.initialState,
};

const slice = createSlice({
  name: "kdrain-summary",
  initialState,
  reducers: {
    addProjectSummary: (
      state,
      action: PayloadAction<{
        id: ProjectId;
        summary: DetailedKDrainProjectSummary;
      }>,
    ) => {
      const id = action.payload.id;
      if (!state.ids.includes(id)) state.ids.push(id);
      state.project[id] = action.payload
        .summary as Draft<DetailedKDrainProjectSummary>;
    },
    setProjectsSummary: (
      state,
      action: PayloadAction<{ year: number; data: GlobalKDrainProjectSummary }>,
    ) => {
      const year = action.payload.year;
      if (!state.ids.includes(year)) state.ids.push(year);
      state.total[year] = action.payload.data;
    },
    setPending: requestStateAdapter.setPending,
    setError: requestStateAdapter.setError,
  },
});

const getActions = (endpointSelector: (api: API) => EndpointSummary) => {
  const { setPending, addProjectSummary, setProjectsSummary } = slice.actions;

  const getKDrainProjectDashboard =
    (id: ProjectId): AppThunk<Promise<DetailedKDrainProjectSummary>> =>
    (dispatch, getState, { api }) => {
      dispatch(setPending(id, true));
      const getPromise = endpointSelector(api).getKDrainProjectDashboard(id);
      getPromise
        .then((entity) => {
          return dispatch(addProjectSummary({ id: id, summary: entity }));
        })
        .catch((err) => dispatch(updateErrorNotification(err)))
        .finally(() => dispatch(setPending(id, false)));
      return getPromise;
    };

  const getKDrainTotalDashboard =
    (year: number): AppThunk<Promise<GlobalKDrainProjectSummary>> =>
    (dispatch, getState, { api }) => {
      dispatch(setPending(totalDashboardId, true));
      const getPromise = endpointSelector(api).getKDrainTotalDashboard(year);
      getPromise
        .then((entity) => {
          return dispatch(setProjectsSummary({ year: year, data: entity }));
        })
        .catch((err) => dispatch(updateErrorNotification(err)))
        .finally(() => dispatch(setPending(totalDashboardId, false)));
      return getPromise;
    };

  return {
    getKDrainProjectDashboard,
    getKDrainTotalDashboard,
  };
};

const getSelectors = () => {
  return {
    selectProjectSummaryById: (id: ProjectId) => (state: RootState) =>
      state.kDrainSummary.project[id],
    selectTotalSummary: (year: number) => (state: RootState) =>
      state.kDrainSummary.total[year],
    selectByIdRequestPending: (id: ProjectId) => (state: RootState) =>
      state.kDrainSummary.pending[id],
    selectByIdRequestError: (id: ProjectId) => (state: RootState) =>
      state.kDrainSummary.error[id],
  };
};

export const kDrainSummaryActions = getActions((api) => api.summary);
export const kDrainSummarySelectors = getSelectors();
export default slice.reducer;
